import { Component } from '@theme/component';
import { prefersReducedMotion, ResizeNotifier, yieldToMainThread } from '@theme/utilities';

/**
 * Applies optional masked reveal or blur animations to a text block.
 *
 * Reveal splits copy into visual lines and clips each line so the inner
 * text can slide up from behind a mask.
 *
 * @extends {Component}
 */
class TextReveal extends Component {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {number} */
  #generation = 0;

  /** @type {number} */
  #lastWidth = -1;

  /** @type {WeakMap<HTMLElement, string>} */
  #originalHTML = new WeakMap();

  connectedCallback() {
    super.connectedCallback();
    void this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation += 1;
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver.disconnect();
  }

  async #init() {
    const generation = ++this.#generation;
    const effect = this.dataset.textEffect;
    const skipAnimation =
      this.dataset.designMode === 'true' || prefersReducedMotion() || !effect || effect === 'none';

    if (effect === 'reveal' && this.dataset.designMode !== 'true') {
      if (document.fonts?.status !== 'loaded') {
        await document.fonts.ready;
      }

      if (generation !== this.#generation || !this.isConnected) return;

      this.#wrapLines();
      this.#lastWidth = this.offsetWidth;
      this.#resizeObserver.observe(this);
    }

    if (skipAnimation) {
      this.classList.add('text-reveal--visible');
      return;
    }

    this.classList.add('text-reveal--prepared');
    this.#observe();
  }

  #wrapLines() {
    const textBlock = this.querySelector('.text-block');

    if (!(textBlock instanceof HTMLElement)) return;

    const children = [...textBlock.children].filter(
      (child) => child instanceof HTMLElement && child.tagName !== 'STYLE'
    );

    if (children.length > 0) {
      for (const child of children) {
        this.#maskElement(child);
      }

      return;
    }

    if (textBlock.childNodes.length > 0) {
      this.#maskElement(textBlock);
    }
  }

  /**
   * @param {HTMLElement} element
   */
  #maskElement(element) {
    const originalHTML = this.#originalHTML.get(element);

    if (originalHTML != null) {
      element.innerHTML = originalHTML;
    } else {
      this.#originalHTML.set(element, element.innerHTML);
    }

    this.#wrapWords(element);
    this.#groupVisualLines(element);
  }

  /**
   * @param {Node} node
   */
  #wrapWords(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';

      if (!text || !node.parentNode) return;

      const parts = text.split(/(\s+)/);
      const fragment = document.createDocumentFragment();

      for (const part of parts) {
        if (part === '') continue;

        if (/^\s+$/.test(part)) {
          fragment.append(document.createTextNode(part));
          continue;
        }

        const word = document.createElement('span');
        word.className = 'text-reveal__word';
        word.textContent = part;
        fragment.append(word);
      }

      node.parentNode.replaceChild(fragment, node);
      return;
    }

    if (!(node instanceof HTMLElement)) return;
    if (node.tagName === 'BR' || node.tagName === 'STYLE') return;
    if (node.classList.contains('text-reveal__word')) return;

    for (const child of [...node.childNodes]) {
      this.#wrapWords(child);
    }
  }

  /**
   * @param {HTMLElement} element
   */
  #groupVisualLines(element) {
    const words = [...element.querySelectorAll('.text-reveal__word')].filter(
      (word) => word instanceof HTMLElement
    );

    if (words.length === 0) return;

    /** @type {HTMLElement[][]} */
    const lines = [];
    /** @type {HTMLElement[]} */
    let current = [];
    let currentTop = Number.NaN;

    for (const word of words) {
      const previous = current[current.length - 1];

      if (previous && this.#hasBreakBetween(previous, word)) {
        lines.push(current);
        current = [word];
        currentTop = word.getBoundingClientRect().top;
        continue;
      }

      const top = word.getBoundingClientRect().top;

      if (current.length === 0) {
        current = [word];
        currentTop = top;
        continue;
      }

      if (Math.abs(top - currentTop) > 1) {
        lines.push(current);
        current = [word];
        currentTop = top;
      } else {
        current.push(word);
      }
    }

    if (current.length > 0) {
      lines.push(current);
    }

    this.#wrapLineGroups(element, lines);
  }

  /**
   * @param {Node} start
   * @param {Node} end
   * @returns {boolean}
   */
  #hasBreakBetween(start, end) {
    const range = document.createRange();
    range.setStartAfter(start);
    range.setEndBefore(end);

    return Boolean(range.cloneContents().querySelector('br'));
  }

  /**
   * @param {HTMLElement} element
   * @param {HTMLElement[][]} lines
   */
  #wrapLineGroups(element, lines) {
    for (let index = lines.length - 1; index >= 0; index--) {
      const words = lines[index];
      const first = words?.[0];
      const last = words?.[words.length - 1];

      if (!first || !last) continue;

      const range = document.createRange();
      range.setStartBefore(first);
      range.setEndAfter(last);

      const line = document.createElement('span');
      line.className = 'text-reveal__line';
      line.style.setProperty('--line-index', String(index));
      line.setAttribute('data-testid', 'text-reveal-line');

      const inner = document.createElement('span');
      inner.className = 'text-reveal__line-inner';
      inner.append(range.extractContents());
      line.append(inner);
      range.insertNode(line);
    }

    for (const br of [...element.querySelectorAll('br')]) {
      br.remove();
    }
  }

  #observe() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];

        if (!entry) return;

        if (entry.intersectionRatio >= 0.3) {
          void this.#play();

          if (this.dataset.animationRepeat === 'false') {
            this.#intersectionObserver?.unobserve(entry.target);
          }
        } else if (this.dataset.animationRepeat !== 'false') {
          this.classList.remove('text-reveal--ready', 'text-reveal--visible');
        }
      },
      { threshold: [0, 0.3] }
    );

    this.#intersectionObserver.observe(this);
  }

  async #play() {
    this.classList.add('text-reveal--ready');
    await yieldToMainThread();
    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve(undefined));
      });
    });
    this.classList.add('text-reveal--visible');
  }

  #resizeObserver = new ResizeNotifier((entries) => {
    const width = entries[0]?.contentRect.width ?? this.offsetWidth;

    if (Math.abs(width - this.#lastWidth) < 1) return;

    this.#lastWidth = width;
    this.#wrapLines();
  });
}

if (!customElements.get('text-reveal')) {
  customElements.define('text-reveal', TextReveal);
}
